/**
 * PORTED — RO-3-04 / #44297.
 * Origin: `excellent/app/customers/customers-dashboard.tsx`.
 *
 * Changes:
 *   next/link → RouterLink
 *   next/navigation useSearchParams → @excellent/ui/router useSearch
 *   ScopedTask type from @excellent/ui/types/projects (same re-export path as
 *     the shell's @/projects/types)
 * Live Next original UNTOUCHED (D6).
 */

import * as React from "react";
import {
  Band,
  Button,
  IconTooltip,
  IntentDot,
  Ledger,
  MetricStrip,
  ScopedSearchButton,
  Card,
  Section,
  ScopedTaskLifecycleList,
  ShellActionsPortal,
  formatNumber,
  formatRelativeTime,
  formatUsd,
  objectHref,
  type DataTableColumn,
  type MetricCell,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { RouterLink as Link, useSearch } from "@excellent/ui/router";
// useSearch() is untyped at the seam; shell-wide `?q=` is the dock query.
import type { ScopedTask } from "@excellent/ui/types/projects";
import {
  CUSTOMER_BAND_INTENT,
  CUSTOMER_BAND_LABEL,
} from "@excellent/ui/blocks/crm/customer-layers";
import type {
  CustomerAccountRow,
  CustomerPortfolioMetrics,
  CustomerUrgentAccount,
} from "@excellent/ui/api/contracts/customers";

export type AccountRow = CustomerAccountRow;
export type PortfolioMetrics = CustomerPortfolioMetrics;
export type UrgentAccount = CustomerUrgentAccount;

type ScopeKey = "ltv" | "pipeline" | "atrisk";

const SCOPE_MATCH: Record<ScopeKey, (r: AccountRow) => boolean> = {
  ltv: (r) => r.lifetimeMinor > 0,
  pipeline: (r) => r.openDeals > 0,
  atrisk: (r) => r.band === "at_risk",
};

const usd = (minor: number) => formatUsd(minor / 100);

const PORTFOLIO_COLUMNS: DataTableColumn<AccountRow>[] = [
  {
    key: "account",
    header: "Account",
    width: "w-4/12",
    render: (r) => (
      <span className="flex items-center gap-2">
        <IntentDot
          intent={CUSTOMER_BAND_INTENT[r.band]}
          size="sm"
          title={CUSTOMER_BAND_LABEL[r.band]}
        />
        <span className="truncate font-medium text-foreground">{r.name}</span>
      </span>
    ),
    sort: (a, b) => a.name.localeCompare(b.name),
  },
  {
    key: "ltv",
    header: "Lifetime value",
    width: "w-3/12",
    render: (r) =>
      r.lifetimeMinor > 0 ? (
        usd(r.lifetimeMinor)
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    sort: (a, b) => a.lifetimeMinor - b.lifetimeMinor,
    defaultDesc: true,
  },
  {
    key: "deals",
    header: "Open deals",
    width: "w-2/12",
    render: (r) => formatNumber(r.openDeals),
    sort: (a, b) => a.openDeals - b.openDeals,
    defaultDesc: true,
  },
  {
    key: "touch",
    header: "Last touch",
    width: "w-3/12",
    render: (r) => (
      <span className="text-muted-foreground">
        {r.lastTouchAt ? formatRelativeTime(r.lastTouchAt) : "—"}
      </span>
    ),
    sort: (a, b) => (a.lastTouchAt ?? "").localeCompare(b.lastTouchAt ?? ""),
    defaultDesc: true,
  },
];

export function CustomersDashboard({
  rows,
  metrics,
  urgent,
  tasks,
  newDealHref,
  noun,
}: {
  rows: AccountRow[];
  metrics: PortfolioMetrics;
  urgent: UrgentAccount | null;
  tasks: ScopedTask[];
  newDealHref: string;
  noun: { singular: string; plural: string };
}) {
  const search = useSearch() as { q?: string };
  const query = search.q ?? "";
  const [scope, setScope] = React.useState<ScopeKey | null>(null);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (scope ? SCOPE_MATCH[scope](r) : true) &&
        (!q || r.name.toLowerCase().includes(q)),
    );
  }, [rows, query, scope]);

  const cells: MetricCell[] = [
    {
      key: "ltv",
      label: "Lifetime value",
      value: usd(metrics.lifetimeMinor),
      context:
        metrics.payingAccounts === 1
          ? "1 paying account"
          : `${formatNumber(metrics.payingAccounts)} paying accounts`,
    },
    {
      key: "pipeline",
      label: "Open pipeline",
      value: usd(metrics.pipelineMinor),
      context:
        metrics.openDealCount === 1
          ? "1 deal in flight"
          : `${formatNumber(metrics.openDealCount)} deals in flight`,
    },
    {
      key: "atrisk",
      label: "At risk",
      value: formatNumber(metrics.atRiskCount),
      context: `${usd(metrics.atRiskLifetimeMinor)} lifetime value exposed`,
    },
  ];

  const urgentClause = urgent
    ? urgent.lifetimeMinor > 0
      ? `${usd(urgent.lifetimeMinor)} lifetime value`
      : urgent.openDealValueMinor > 0
        ? `${usd(urgent.openDealValueMinor)} in open deals`
        : null
    : null;

  return (
    <Band>
      <ShellActionsPortal>
        <div className="flex items-center gap-1.5">
          <ScopedSearchButton label={`Search ${noun.plural.toLowerCase()}`} />
          <IconTooltip label="New deal">
            <Button asChild variant="default" size="icon-tb" aria-label="New deal">
              <Link href={newDealHref}>
                <Plus strokeWidth={2.25} />
              </Link>
            </Button>
          </IconTooltip>
        </div>
      </ShellActionsPortal>

      {urgent ? (
        <p className="flex items-center gap-2 type-body text-foreground">
          <IntentDot intent="danger" size="sm" />
          <span className="min-w-0 truncate">
            {urgent.name} is at risk
            {urgentClause ? ` — ${urgentClause},` : " —"}{" "}
            {urgent.lastTouchAt
              ? `last touch ${formatRelativeTime(urgent.lastTouchAt)}`
              : "no recent touch"}
            .
          </span>
        </p>
      ) : null}

      <Section label="Portfolio">
        <Ledger
          rows={filtered}
          columns={PORTFOLIO_COLUMNS}
          rowKey={(r) => r.uid}
          tableClassName="table-fixed"
          strip={
            <MetricStrip
              cells={cells}
              selected={scope ?? undefined}
              onSelect={(key) =>
                setScope((cur) => (cur === key ? null : (key as ScopeKey)))
              }
            />
          }
          initialSort={{ key: "ltv", dir: "desc" }}
          rowHref={(r) => objectHref(r.uid, "customers")}
          empty={`No ${noun.plural.toLowerCase()} match.`}
        />
      </Section>

      <Section label="Issues">
        <Card className="gap-0 overflow-hidden py-0">
          <ScopedTaskLifecycleList
            tasks={tasks}
            empty="No open issues in marketing, sales, or customer success."
            gutter
          />
        </Card>
      </Section>
    </Band>
  );
}
